// src/standalone.ts
/**
 * Standalone terminal entry point
 * Renders a full-screen terminal without React overhead
 * 
 * Requirements: 12.2 - Standalone Mode, 13.1 - E2EE Key Exchange
 */

import { ConnectionManager } from './connection/websocket';
import { TerminalManager } from './core/terminal-manager';
import { decodeData } from './connection/protocol';
import { THEMES } from './theme/presets';
import { acceptNewKey } from './e2ee/tofu';
import { FileExplorerVanilla } from './components/FileExplorer';
import { TabBarVanilla } from './components/TabBar';
import { ScrollbackBannerVanilla, ScrollbackToggleVanilla } from './components/Scrollback';
import { createTabStore } from './stores/tabStore';
import type { TabStore } from './stores/tabStore';
import type { ConnectionState, SessionEndReason, RelayToBrowserMessage, FileEntry, FileError, SortBy, SortOrder } from './types';
import type { E2EEState, KeyExchangeResult } from './e2ee/types';

// Import xterm.js CSS
import '@xterm/xterm/css/xterm.css';

// Import WHSS terminal styles
import './styles/terminal.css';
import './styles/mobile.css';
import './styles/accessibility.css';
import './styles/e2ee.css';
import './styles/file-explorer.css';
import './components/TabBar/tab-bar.css';
import './components/Scrollback/scrollback.css';

/**
 * Configuration for standalone terminal
 */
interface StandaloneConfig {
  relayUrl: string;
  token: string;
  theme?: string;
  authUrl?: string;
  /** Agent's E2EE public key (base64-encoded X25519 key) */
  agentPublicKey?: string;
  /** Subdomain for TOFU key verification */
  subdomain?: string;
}

/**
 * Get token from cookie or sessionStorage.
 *
 * NOTE: Tokens are NOT accepted from URL query parameters to prevent
 * leakage via Referer headers, browser history, and server logs.
 * The dashboard passes tokens via secure httpOnly cookies instead.
 */
function getToken(): string | null {
  // Check sessionStorage (set by dashboard before redirect)
  const storedToken = sessionStorage.getItem('whss_session_token');
  if (storedToken) {
    return storedToken;
  }

  // Check cookie
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'whss_session_token' && value) {
      return decodeURIComponent(value);
    }
  }

  return null;
}

/**
 * Get agent public key from sessionStorage, data attribute, or meta tag.
 *
 * NOTE: No longer accepted from URL query parameters to prevent leakage.
 * Legacy URL params are migrated to sessionStorage in main().
 */
function getAgentPublicKey(): string | null {
  // Check sessionStorage (migrated from URL params or set by dashboard)
  const storedKey = sessionStorage.getItem('whss_agent_public_key');
  if (storedKey) {
    return storedKey;
  }

  // Check data attribute on root element
  const root = document.getElementById('terminal-root');
  if (root?.dataset.agentPublicKey) {
    return root.dataset.agentPublicKey;
  }

  // Check meta tag
  const meta = document.querySelector('meta[name="whss-agent-public-key"]');
  if (meta) {
    return meta.getAttribute('content') || null;
  }

  return null;
}

/**
 * Get subdomain from hostname, URL path, query parameter, or data attribute
 * Hostname format: {subdomain}.whss.dev
 * URL format: /{subdomain} or /terminal/{subdomain}
 */
function getSubdomain(): string | null {
  // Check hostname first (e.g., gamd.whss.dev -> gamd)
  const hostname = window.location.hostname;
  const hostMatch = hostname.match(/^([a-z0-9][a-z0-9-]*[a-z0-9]?)\.whss\.dev$/);
  if (hostMatch && hostMatch[1] && !['terminal', 'app', 'auth', 'relay', 'www'].includes(hostMatch[1])) {
    return hostMatch[1];
  }

  // Check URL query parameter
  const urlParams = new URLSearchParams(window.location.search);
  const urlSubdomain = urlParams.get('subdomain');
  if (urlSubdomain) {
    return urlSubdomain;
  }

  // Check data attribute on root element
  const root = document.getElementById('terminal-root');
  if (root?.dataset.subdomain) {
    return root.dataset.subdomain;
  }

  // Check meta tag
  const meta = document.querySelector('meta[name="whss-subdomain"]');
  if (meta) {
    return meta.getAttribute('content') || null;
  }

  // Extract from URL path
  const path = window.location.pathname;
  
  // Match /terminal/{subdomain} or /{subdomain}
  const terminalMatch = path.match(/^\/terminal\/([a-z0-9][a-z0-9-]*[a-z0-9]?)$/);
  if (terminalMatch && terminalMatch[1]) {
    return terminalMatch[1];
  }

  const directMatch = path.match(/^\/([a-z0-9][a-z0-9-]*[a-z0-9]?)$/);
  if (directMatch && directMatch[1]) {
    return directMatch[1];
  }

  return null;
}

/**
 * Get relay URL from configuration or default
 */
function getRelayUrl(): string {
  // Check for data attribute on root element
  const root = document.getElementById('terminal-root');
  if (root?.dataset.relayUrl) {
    return root.dataset.relayUrl;
  }

  // Check meta tag (only if it has a non-empty value)
  const meta = document.querySelector('meta[name="whss-relay-url"]');
  if (meta) {
    const content = meta.getAttribute('content');
    if (content) {
      return content;
    }
  }

  // Get subdomain for URL construction
  const subdomain = getSubdomain();
  
  // If on agent subdomain (gamd.whss.dev), use current host
  // The relay serves both WebSocket and proxies terminal HTML on *.whss.dev
  const hostname = window.location.hostname;
  const firstPart = hostname.split('.')[0] || '';
  const isAgentSubdomain = hostname.match(/^([a-z0-9][a-z0-9-]*[a-z0-9]?)\.whss\.dev$/) &&
    !['terminal', 'app', 'auth', 'relay', 'www'].includes(firstPart);
  
  if (isAgentSubdomain && subdomain) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}/ws/${subdomain}`;
  }

  // Default: use relay.whss.dev with subdomain path
  if (subdomain) {
    return `wss://relay.whss.dev/ws/${subdomain}`;
  }

  // Fallback to current host
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/ws`;
}

/**
 * Get auth URL for redirect
 */
function getAuthUrl(): string {
  const root = document.getElementById('terminal-root');
  if (root?.dataset.authUrl) {
    return root.dataset.authUrl;
  }

  const meta = document.querySelector('meta[name="whss-auth-url"]');
  if (meta) {
    const content = meta.getAttribute('content');
    if (content) return content;
  }

  // Default to dashboard
  return 'https://app.whss.dev';
}

/**
 * Redirect to auth page with return URL
 */
function redirectToAuth(authUrl: string): void {
  // Redirect to dashboard - user needs to click Connect to get a new session token
  window.location.href = `${authUrl}/agents`;
}

/**
 * Create status indicator element
 */
function createStatusIndicator(): HTMLElement {
  // Status indicator already exists in HTML, just return it
  const indicator = document.getElementById('connection-status');
  if (indicator) {
    return indicator;
  }
  // Fallback: create if not found
  const newIndicator = document.createElement('div');
  newIndicator.id = 'connection-status';
  newIndicator.className = 'status-indicator status-disconnected';
  newIndicator.innerHTML = '<span class="status-dot"></span><span class="status-text">Disconnected</span>';
  return newIndicator;
}

/**
 * Update status indicator
 */
function updateStatus(indicator: HTMLElement, state: ConnectionState, sessionId?: string): void {
  // Remove all status classes
  indicator.className = 'status-indicator';
  indicator.classList.add(`status-${state}`);
  
  let text = state.charAt(0).toUpperCase() + state.slice(1);
  if (sessionId && state === 'ready') {
    text = `Connected (${sessionId.slice(0, 8)})`;
  }
  
  const textEl = indicator.querySelector('.status-text');
  if (textEl) {
    textEl.textContent = text;
  }
}

/**
 * Get or create E2EE indicator element
 */
function getE2EEIndicator(): HTMLElement {
  const indicator = document.getElementById('e2ee-indicator');
  if (indicator) {
    return indicator;
  }
  // Fallback: create if not found
  const newIndicator = document.createElement('span');
  newIndicator.id = 'e2ee-indicator';
  newIndicator.className = 'e2ee-badge hidden';
  newIndicator.innerHTML = '<span class="e2ee-icon">🔒</span><span class="e2ee-text">E2EE</span>';
  return newIndicator;
}

/**
 * Update E2EE indicator
 */
function updateE2EEIndicator(
  indicator: HTMLElement, 
  state: E2EEState, 
  fingerprint?: string | null
): void {
  // Remove all state classes
  indicator.classList.remove('active', 'pending', 'failed', 'hidden');
  
  if (state === 'active') {
    indicator.classList.add('active');
    const textEl = indicator.querySelector('.e2ee-text');
    if (textEl) {
      textEl.textContent = fingerprint ? `E2EE (${fingerprint.slice(0, 8)})` : 'E2EE';
    }
  } else if (state === 'key_exchange_pending') {
    indicator.classList.add('pending');
    const textEl = indicator.querySelector('.e2ee-text');
    if (textEl) {
      textEl.textContent = 'E2EE...';
    }
  } else if (state === 'failed') {
    indicator.classList.add('failed');
    const iconEl = indicator.querySelector('.e2ee-icon');
    const textEl = indicator.querySelector('.e2ee-text');
    if (iconEl) iconEl.textContent = '⚠️';
    if (textEl) textEl.textContent = 'E2EE Failed';
  } else {
    indicator.classList.add('hidden');
  }
}

/**
 * Create key change warning overlay
 */
function createKeyChangeWarning(): HTMLElement {
  const overlay = document.createElement('div');
  overlay.id = 'key-change-warning';
  overlay.className = 'key-change-warning hidden';
  overlay.style.display = 'none'; // Explicitly hide initially
  overlay.setAttribute('role', 'alertdialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'key-change-title');
  
  overlay.innerHTML = `
    <div class="key-change-content">
      <h2 id="key-change-title" class="key-change-title">⚠️ Security Warning</h2>
      <p class="key-change-message">
        The encryption key for this agent has changed since your last connection.
        This could indicate:
      </p>
      <ul class="key-change-reasons">
        <li>The agent was reinstalled or reconfigured</li>
        <li>You're connecting to a different machine</li>
        <li>A potential security issue (man-in-the-middle attack)</li>
      </ul>
      <div class="key-change-fingerprints">
        <div class="fingerprint-row">
          <span class="fingerprint-label">Previous key:</span>
          <code class="fingerprint-value" id="previous-fingerprint"></code>
        </div>
        <div class="fingerprint-row">
          <span class="fingerprint-label">New key:</span>
          <code class="fingerprint-value" id="new-fingerprint"></code>
        </div>
      </div>
      <p class="key-change-advice">
        If you expected this change, you can accept the new key. Otherwise, do not connect.
      </p>
      <div class="key-change-actions">
        <button class="key-change-reject" id="reject-key">Reject & Disconnect</button>
        <button class="key-change-accept" id="accept-key">Accept New Key</button>
      </div>
    </div>
  `;
  
  return overlay;
}

/**
 * Show key change warning
 */
function showKeyChangeWarning(
  overlay: HTMLElement,
  result: KeyExchangeResult,
  onAccept: () => void,
  onReject: () => void
): void {
  const previousEl = overlay.querySelector('#previous-fingerprint');
  const newEl = overlay.querySelector('#new-fingerprint');
  const acceptBtn = overlay.querySelector('#accept-key') as HTMLButtonElement;
  const rejectBtn = overlay.querySelector('#reject-key') as HTMLButtonElement;
  
  if (previousEl && result.previousFingerprint) {
    previousEl.textContent = result.previousFingerprint;
  }
  if (newEl && result.newFingerprint) {
    newEl.textContent = result.newFingerprint;
  }
  
  if (acceptBtn) {
    acceptBtn.onclick = () => {
      overlay.classList.add('hidden');
      overlay.style.display = 'none';
      onAccept();
    };
  }
  
  if (rejectBtn) {
    rejectBtn.onclick = () => {
      overlay.classList.add('hidden');
      overlay.style.display = 'none';
      onReject();
    };
  }
  
  overlay.classList.remove('hidden');
  overlay.style.display = 'flex';
}

/**
 * Create error overlay element
 */
function createErrorOverlay(): HTMLElement {
  const overlay = document.createElement('div');
  overlay.id = 'error-overlay';
  overlay.className = 'error-overlay hidden';
  overlay.style.display = 'none'; // Explicitly hide initially
  overlay.setAttribute('role', 'alert');
  
  const content = document.createElement('div');
  content.className = 'error-content';
  
  const title = document.createElement('h2');
  title.className = 'error-title';
  title.textContent = 'Connection Error';
  
  const message = document.createElement('p');
  message.className = 'error-message';
  
  const retryButton = document.createElement('button');
  retryButton.className = 'error-retry';
  retryButton.textContent = 'Retry';
  
  content.appendChild(title);
  content.appendChild(message);
  content.appendChild(retryButton);
  overlay.appendChild(content);
  
  return overlay;
}

/**
 * Show error overlay
 */
function showError(overlay: HTMLElement, message: string, onRetry?: () => void): void {
  const messageEl = overlay.querySelector('.error-message');
  const retryButton = overlay.querySelector('.error-retry') as HTMLButtonElement;
  
  if (messageEl) {
    messageEl.textContent = message;
  }
  
  if (retryButton && onRetry) {
    retryButton.onclick = () => {
      hideError(overlay);
      onRetry();
    };
    retryButton.style.display = 'block';
  } else if (retryButton) {
    retryButton.style.display = 'none';
  }
  
  overlay.classList.remove('hidden');
  overlay.style.display = 'flex';
}

/**
 * Hide error overlay
 */
function hideError(overlay: HTMLElement): void {
  overlay.classList.add('hidden');
  overlay.style.display = 'none';
}

/**
 * Initialize standalone terminal
 */
function initStandalone(config: StandaloneConfig): void {
  const container = document.getElementById('terminal-root');
  if (!container) {
    console.error('Terminal root element not found');
    return;
  }

  // Get existing UI elements from HTML
  const statusIndicator = createStatusIndicator();
  const e2eeIndicator = getE2EEIndicator();
  
  // Create error overlay and key change warning
  const errorOverlay = createErrorOverlay();
  const keyChangeWarning = createKeyChangeWarning();
  
  container.appendChild(errorOverlay);
  document.body.appendChild(keyChangeWarning);

  // Get theme
  const themeId = config.theme || 'whss-dark';
  const themeColors = THEMES[themeId]?.colors;

  // Get terminal container (already exists in HTML)
  const terminalContainer = document.getElementById('terminal-container');
  if (!terminalContainer) {
    console.error('Terminal container not found');
    return;
  }
  
  // Remove loading indicator
  const loadingEl = terminalContainer.querySelector('.loading');
  if (loadingEl) {
    loadingEl.remove();
  }

  // Create main content wrapper for sidebar + terminal layout
  const mainContent = document.createElement('div');
  mainContent.className = 'whss-main-content';
  
  // Create sidebar container for FileExplorer
  const sidebarContainer = document.createElement('div');
  sidebarContainer.id = 'file-explorer-container';
  sidebarContainer.className = 'whss-sidebar-container';
  
  // Create terminal wrapper
  const terminalWrapper = document.createElement('div');
  terminalWrapper.className = 'whss-terminal-wrapper';
  
  // Insert main content before terminal container, then move terminal into it
  terminalContainer.parentNode?.insertBefore(mainContent, terminalContainer);
  mainContent.appendChild(sidebarContainer);
  terminalWrapper.appendChild(terminalContainer);
  mainContent.appendChild(terminalWrapper);

  // Create tab bar container (between header and main-content)
  const tabBarContainer = document.createElement('div');
  tabBarContainer.className = 'tb-container';
  mainContent.parentNode?.insertBefore(tabBarContainer, mainContent);

  // Create scrollback banner container (between tab bar and main-content)
  const scrollbackBannerContainer = document.createElement('div');
  scrollbackBannerContainer.className = 'sb-banner-container';
  mainContent.parentNode?.insertBefore(scrollbackBannerContainer, mainContent);

  // Create scrollback toggle container (in header status-bar-right)
  const scrollbackToggleContainer = document.createElement('div');
  scrollbackToggleContainer.className = 'sb-toggle-container';
  const statusBarRight = container.querySelector('.status-bar-right');
  if (statusBarRight) {
    statusBarRight.insertBefore(scrollbackToggleContainer, statusBarRight.firstChild);
  }

  let sessionId: string | null = null;
  let e2eeState: E2EEState = 'not_initialized';
  let fileExplorer: FileExplorerVanilla | null = null;
  let tabStore: TabStore | null = null;
  let tabBar: TabBarVanilla | null = null;
  let scrollbackBanner: ScrollbackBannerVanilla | null = null;
  let scrollbackToggle: ScrollbackToggleVanilla | null = null;

  // We need a forward reference to connection for the TerminalManager callbacks.
  // connection is assigned immediately after terminalManager creation.
  let connection: ConnectionManager;

  // Create TerminalManager — one SessionController per tab, routes I/O by session_id
  const terminalManager = new TerminalManager({
    parentContainer: terminalContainer,
    ...(themeColors ? { defaultTheme: themeColors } : {}),
    onInput: (sid: string, data: string) => {
      if (connection.isE2EEActive(sid)) {
        connection.sendEncryptedInput(data, sid);
      } else {
        connection.sendInput(data, sid);
      }
    },
    onResize: (sid: string, rows: number, cols: number) => {
      connection.sendResize(rows, cols, sid);
    },
    onActivity: (sid: string) => {
      tabStore?.markActivity(sid);
    },
  });

  // Create connection
  connection = new ConnectionManager(
    {
      relayUrl: config.relayUrl,
      token: config.token,
      autoReconnect: true,
    },
    {
      onStateChange: (state: ConnectionState) => {
        updateStatus(statusIndicator, state, sessionId || undefined);
        
        if (state === 'error') {
          showError(errorOverlay, 'Connection lost. Please check your network.', () => {
            connection.connect();
          });
        } else {
          hideError(errorOverlay);
        }
      },
      onMessage: (msg: RelayToBrowserMessage) => {
        if (msg.type === 'output') {
          // Route output to the correct session's terminal
          const targetSid = msg.session_id ?? terminalManager.getActiveSessionId();
          if (targetSid) {
            const bytes = decodeData(msg.data);
            terminalManager.routeOutput(targetSid, bytes);
          }
        } else if (msg.type === 'dir_contents') {
          // Handle directory contents for FileExplorer (shared, active session only)
          const dirMsg = msg as { session_id: string; path: string; entries: FileEntry[]; truncated: boolean; error: FileError | null };
          fileExplorer?.updateContents(dirMsg.path, dirMsg.entries, dirMsg.truncated, dirMsg.error);
        } else if (msg.type === 'cwd_changed') {
          // Handle CWD change for FileExplorer — only update if it's the active session.
          // When E2EE is active, the decrypted payload may not include session_id
          // (it's on the outer EncryptedFileData envelope, stripped during decryption).
          // Treat missing session_id as "from active session" (BUG-053).
          const cwdMsg = msg as { session_id?: string; path: string };
          const activeSid = terminalManager.getActiveSessionId();
          if (!activeSid || !cwdMsg.session_id || cwdMsg.session_id === activeSid) {
            fileExplorer?.updateCwd(cwdMsg.path);
          }
        } else if (msg.type === 'scrollback_data') {
          // Route scrollback to the correct session's terminal
          const sbMsg = msg as { session_id: string; lines: string[]; total_lines: number; oldest_timestamp?: number; is_live: boolean };
          if (sbMsg.lines.length > 0) {
            terminalManager.routeScrollback(sbMsg.session_id, sbMsg.lines, sbMsg.total_lines, sbMsg.oldest_timestamp);
            // Show banner if this is the active session
            if (sbMsg.session_id === terminalManager.getActiveSessionId()) {
              scrollbackBanner?.show(sbMsg.total_lines, sbMsg.oldest_timestamp);
            }
          }
        } else if (msg.type === 'session_created') {
          // A new session was created for a tab (new tab or reconnect)
          const scMsg = msg as { tab_id: string; session_id: string; restored: boolean; scrollback_available?: boolean };
          
          // Check if this tab already has a terminal from a previous session (reconnect case)
          const tab = tabStore?.getTabById(scMsg.tab_id);
          const oldSid = tab?.previousSessionId ?? tab?.sessionId;
          
          tabStore?.handleSessionCreated(
            scMsg.tab_id,
            scMsg.session_id,
            scMsg.restored,
            scMsg.scrollback_available ?? false
          );
          
          if (oldSid && terminalManager.getSession(oldSid)) {
            // Transfer existing terminal to new session ID (preserves content)
            terminalManager.transferSession(oldSid, scMsg.session_id);
          } else {
            // Brand new tab — create fresh terminal
            terminalManager.createSession(scMsg.session_id);
          }
          
          // If this is the active tab, switch to it
          const activeTab = tabStore?.getActiveTab();
          if (activeTab && activeTab.sessionId === scMsg.session_id) {
            terminalManager.switchTo(scMsg.session_id);
            // Refit after DOM settles — ensures correct row count for TUI apps
            // like micro that render on the last row (BUG-051)
            requestAnimationFrame(() => {
              const size = terminalManager.fitActive();
              connection.sendResize(size.rows, size.cols, scMsg.session_id);
            });
          }
          // Update scrollback toggle with new session
          scrollbackToggle?.setSessionId(scMsg.session_id);
        } else if (msg.type === 'session_closed') {
          // Forward to TabStore, then destroy the SessionController
          const clMsg = msg as { session_id: string; reason: import('./types').CloseReason };
          tabStore?.handleSessionClosed(clMsg.session_id, clMsg.reason);
          terminalManager.destroySession(clMsg.session_id);
        } else if (msg.type === 'session_state_changed') {
          // Forward to TabStore
          const stMsg = msg as { session_id: string; state: import('./types').SessionState };
          tabStore?.handleSessionStateChanged(stMsg.session_id, stMsg.state);
        } else if (msg.type === 'session_ended') {
          // Legacy session_ended — treat like session_closed for the affected tab.
          // Do NOT tear down the whole WebSocket; just clean up that one session.
          const endMsg = msg as { session_id?: string; reason: string; exit_code?: number };
          const endedSid = endMsg.session_id ?? sessionId;
          if (endedSid) {
            const tab = tabStore?.getTabBySessionId(endedSid);
            if (tab) {
              const reason: import('./types').CloseReason = endMsg.exit_code !== undefined
                ? { process_exited: { exit_code: endMsg.exit_code } }
                : { error: endMsg.reason || 'session ended' };
              tabStore?.handleSessionClosed(endedSid, reason);
              terminalManager.destroySession(endedSid);
            }
          }
        }
        // Note: encrypted_output is handled internally by ConnectionManager
        // and forwarded as 'output' with session_id preserved
      },
      onError: (error: Error) => {
        console.error('Connection error:', error);
        
        // Check for E2EE errors
        if (error.message.includes('Decryption failed') || error.message.includes('E2EE')) {
          e2eeState = 'failed';
          updateE2EEIndicator(e2eeIndicator, e2eeState);
          showError(errorOverlay, 'End-to-end encryption failed. The connection may be compromised.', () => {
            connection.connect();
          });
          return;
        }
        
        // Check for auth errors
        if (error.message.includes('auth_required') || 
            error.message.includes('auth_invalid') ||
            error.message.includes('auth_expired')) {
          showError(errorOverlay, 'Session expired. Redirecting to login...', undefined);
          setTimeout(() => redirectToAuth(config.authUrl || '/login'), 2000);
        }
      },
      onSessionReady: (id: string) => {
        sessionId = id;
        updateStatus(statusIndicator, 'ready', id);
        
        // The relay sends a connection-level session_id in the Connected message.
        // This is NOT a real PTY session — the browser drives all session creation
        // via CreateSession messages (BUG-050 fix).
        //
        // On reconnect/reload, try to restore tabs from localStorage first.
        // restoreTabs() populates the tab list and sends CreateSession with
        // restore_session_id for each persisted tab. The agent will either:
        // - Reattach if the session is still active (brief disconnect)
        // - Restore from scrollback if terminated
        // - Create fresh if the session ID is unknown (agent restarted)
        let existingTabs = tabStore?.getTabs() ?? [];
        
        // If no tabs in memory, try restoring from localStorage (page reload case)
        if (existingTabs.length === 0) {
          const restored = tabStore?.restoreTabs() ?? 0;
          if (restored === 0) {
            // Nothing to restore — first connect ever, create Tab 1
            tabStore?.createTab('Tab 1');
          }
          // restoreTabs() already sent CreateSession for each restored tab,
          // or createTab() sent CreateSession for Tab 1. Either way, done.
        } else {
          // Tabs already in memory — this is a WebSocket reconnect (not page reload).
          // Try to restore tabs that have a session ID.
          const tabsToRestore = existingTabs.filter(
            t => t.status !== 'terminated' && t.sessionId
          );
          
          if (tabsToRestore.length > 0) {
            for (const tab of tabsToRestore) {
              tabStore?.markReconnecting(tab.id);
              connection.createSession(tab.id, tab.sessionId!);
            }
          } else {
            // All tabs are terminated or have no session — clear and start fresh
            tabStore?.clearPersistedTabs();
            tabStore?.reset();
            tabStore?.createTab('Tab 1');
          }
        }
        
        // Update scrollback toggle with session
        scrollbackToggle?.setSessionId(id);
        
        // Update E2EE state
        e2eeState = connection.getE2EEState();
        updateE2EEIndicator(
          e2eeIndicator, 
          e2eeState, 
          connection.getAgentKeyFingerprint()
        );
        
        // Initialize FileExplorer now that session is ready (shared, one instance)
        if (!fileExplorer) {
          fileExplorer = new FileExplorerVanilla({
            container: sidebarContainer,
            onListDirectory: (path: string, options: { showHidden: boolean; sortBy: SortBy; sortOrder: SortOrder }) => {
              connection.listDirectory(path, options);
            },
            onPathInsert: (path: string) => {
              // Insert path into the active terminal
              terminalManager.writeToActive(path);
            },
            onCommand: (command: string) => {
              // Execute command in the active terminal's session
              const activeSid = terminalManager.getActiveSessionId();
              if (connection.isE2EEActive()) {
                connection.sendEncryptedInput(command + '\r', activeSid ?? undefined);
              } else {
                connection.sendInput(command + '\r', activeSid ?? undefined);
              }
            },
            onSidebarToggle: () => {
              // Refit active terminal after sidebar animation settles
              setTimeout(() => {
                const size = terminalManager.fitActive();
                const activeSid = terminalManager.getActiveSessionId();
                connection.sendResize(size.rows, size.cols, activeSid ?? undefined);
              }, 300);
            },
          });
          fileExplorer.initialize();
        }
        
        // If TerminalManager has an active session, refit it
        if (terminalManager.getActiveSessionId()) {
          requestAnimationFrame(() => {
            const size = terminalManager.fitActive();
            const activeSid = terminalManager.getActiveSessionId();
            connection.sendResize(size.rows, size.cols, activeSid ?? undefined);
          });
        }
        
        // Focus active terminal
        terminalManager.focusActive();
      },
      onSessionEnded: (reason: SessionEndReason, exitCode?: number) => {
        sessionId = null;
        
        let message = 'Session ended';
        switch (reason) {
          case 'user_closed':
            message = 'Session closed by user';
            break;
          case 'agent_disconnected':
            message = 'Agent disconnected';
            break;
          case 'idle_timeout':
            message = 'Session timed out due to inactivity';
            break;
          case 'server_shutdown':
            message = 'Server is shutting down';
            break;
          case 'error':
            message = 'Session ended due to an error';
            break;
        }
        
        if (exitCode !== undefined) {
          message += ` (exit code: ${exitCode})`;
        }
        
        showError(errorOverlay, message, () => {
          connection.connect();
        });
      },
    }
  );

  // Create TabStore and TabBar for multi-session tabs
  tabStore = createTabStore({
    onCreateSession: (tabId: string, restoreSessionId?: string) => {
      connection.createSession(tabId, restoreSessionId);
    },
    onCloseSession: (sid: string) => {
      connection.closeSession(sid);
    },
    onActiveTabChanged: (_tabId: string, newSessionId: string | null) => {
      // Switch the visible terminal to the new active tab's session
      if (newSessionId) {
        terminalManager.switchTo(newSessionId);
        // Update scrollback toggle for the new session
        scrollbackToggle?.setSessionId(newSessionId);
        // Refit after DOM settles — ensures correct row count after tab switch (BUG-051)
        requestAnimationFrame(() => {
          const size = terminalManager.fitActive();
          connection.sendResize(size.rows, size.cols, newSessionId);
        });
      }
    },
  });

  tabBar = new TabBarVanilla({
    container: tabBarContainer,
    tabStore,
  });
  tabBar.initialize();
  tabStore.registerKeyboardShortcuts();

  // Create scrollback UI components
  scrollbackBanner = new ScrollbackBannerVanilla({
    container: scrollbackBannerContainer,
    onDismiss: () => {
      // Banner dismissed — no action needed
    },
  });

  scrollbackToggle = new ScrollbackToggleVanilla({
    container: scrollbackToggleContainer,
    onToggle: (enabled: boolean) => {
      const activeSid = terminalManager.getActiveSessionId();
      if (activeSid) {
        connection.setScrollbackEnabled(activeSid, enabled);
      }
    },
  });

  // No standalone terminal.onData() wiring needed —
  // each SessionController wires its own onData → onInput callback

  // Initialize E2EE if agent public key is provided
  if (config.agentPublicKey && config.subdomain) {
    const result = connection.initializeE2EE(config.agentPublicKey, config.subdomain);
    
    if (!result.success && result.keyChanged) {
      // Key has changed - show warning, don't connect yet
      e2eeState = 'failed';
      updateE2EEIndicator(e2eeIndicator, e2eeState);
      
      showKeyChangeWarning(
        keyChangeWarning,
        result,
        () => {
          // User accepted the new key
          acceptNewKey(config.subdomain!, config.agentPublicKey!);
          connection.initializeE2EE(config.agentPublicKey!, config.subdomain!);
          e2eeState = 'key_exchange_pending';
          updateE2EEIndicator(e2eeIndicator, e2eeState);
          connection.connect();
        },
        () => {
          // User rejected the new key - show error and don't connect
          showError(errorOverlay, 'Connection rejected due to key change. Please verify the agent identity.', undefined);
        }
      );
    } else if (result.success) {
      e2eeState = 'key_exchange_pending';
      updateE2EEIndicator(e2eeIndicator, e2eeState);
      // Connect normally
      connection.connect();
    }
  } else {
    // No E2EE - connect without encryption
    connection.connect();
  }

  // Handle page unload
  window.addEventListener('beforeunload', () => {
    scrollbackBanner?.dispose();
    scrollbackToggle?.dispose();
    tabBar?.dispose();
    tabStore?.dispose();
    fileExplorer?.dispose();
    connection.disconnect();
    terminalManager.dispose();
  });

  // Handle visibility change (pause/resume)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      terminalManager.focusActive();
    }
  });
}

/**
 * Get access token from storage (for API calls)
 * Checks sessionStorage first, then falls back to cross-domain cookie
 */
function getAccessToken(): string | null {
  // Check sessionStorage first (same origin)
  const sessionToken = sessionStorage.getItem('whss-access-token');
  if (sessionToken) {
    return sessionToken;
  }

  // Check cross-domain cookie (set by dashboard at .whss.dev)
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'whss_access_token' && value) {
      const token = decodeURIComponent(value);
      // Store in sessionStorage for future use on this origin
      sessionStorage.setItem('whss-access-token', token);
      return token;
    }
  }

  return null;
}

/**
 * Fetch a terminal session token from the auth API
 */
async function fetchSessionToken(subdomain: string): Promise<{
  sessionToken: string;
  agentPublicKey?: string;
} | null> {
  const accessToken = getAccessToken();
  if (!accessToken) {
    return null;
  }

  try {
    // Use /api/sessions/by-subdomain endpoint which accepts subdomain string
    const response = await fetch('https://auth.whss.dev/api/sessions/by-subdomain', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ subdomain }),
    });

    if (response.status === 401) {
      // Access token expired, clear it from both sessionStorage and cookie
      sessionStorage.removeItem('whss-access-token');
      const domain = window.location.hostname.includes('whss.dev') ? '.whss.dev' : window.location.hostname;
      document.cookie = `whss_access_token=; domain=${domain}; path=/; max-age=0; secure; samesite=lax`;
      return null;
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      console.error('Failed to get session token:', error);
      return null;
    }

    const data = await response.json();
    return {
      sessionToken: data.session_token,
      agentPublicKey: data.agent_public_key,
    };
  } catch (error) {
    console.error('Failed to fetch session token:', error);
    return null;
  }
}

/**
 * Main entry point
 */
async function main(): Promise<void> {
  // Strip any sensitive query parameters from the URL to prevent leakage
  // via Referer headers, browser history sync, and server logs
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has('token') || urlParams.has('agent_public_key')) {
    // If token was passed in URL (legacy), move it to sessionStorage and clean URL
    const urlToken = urlParams.get('token');
    const urlKey = urlParams.get('agent_public_key');
    if (urlToken) {
      sessionStorage.setItem('whss_session_token', urlToken);
    }
    if (urlKey) {
      sessionStorage.setItem('whss_agent_public_key', urlKey);
    }
    urlParams.delete('token');
    urlParams.delete('agent_public_key');
    const cleanUrl = urlParams.toString()
      ? `${window.location.pathname}?${urlParams.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, '', cleanUrl);
  }

  // Get subdomain from hostname or URL
  const subdomain = getSubdomain();
  
  if (!subdomain) {
    console.error('No subdomain found');
    // Redirect to dashboard if no subdomain
    window.location.href = 'https://app.whss.dev/agents';
    return;
  }

  // Get token from secure storage (cookie or sessionStorage)
  let token = getToken();
  let agentPublicKey = getAgentPublicKey();

  // If no token in storage, try to fetch one using the user's session
  if (!token) {
    const session = await fetchSessionToken(subdomain);
    
    if (session) {
      token = session.sessionToken;
      agentPublicKey = session.agentPublicKey || null;
    } else {
      // Not logged in or session expired - redirect to login
      const returnUrl = encodeURIComponent(window.location.href);
      window.location.href = `https://app.whss.dev/login?return=${returnUrl}`;
      return;
    }
  }

  // Get relay URL
  const relayUrl = getRelayUrl();
  
  if (!relayUrl) {
    console.error('Relay URL not configured');
    return;
  }

  // Initialize terminal
  initStandalone({
    relayUrl,
    token,
    authUrl: getAuthUrl(),
    ...(agentPublicKey && { agentPublicKey }),
    ...(subdomain && { subdomain }),
  });
}

// Run on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', main);
} else {
  main();
}
